import React, { useState, useContext, useEffect } from 'react';
import {
    makeStyles, Switch, Grid,
    Typography,
} from '@material-ui/core';
import MenuOutlinedIcon from '@material-ui/icons/MenuOutlined';
import NotificationsNoneOutlinedIcon from '@material-ui/icons/NotificationsNoneOutlined';
import ModalUtils from 'utils/ModalUtils';
import UserContext from 'components/context/UserContext';
import PREFERENCES_KEY from 'utils/enum/UserSettingEnum';
import { useMutation } from '@apollo/client';
import SettingsMutation from 'services/graphQL/mutate/SettingsMutation';

const useStyles = makeStyles((theme) => ({
    box: {
        padding: '16px',
        height: 'auto !important',
    },
    header: {
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(2),
        '& > h4': {
            fontSize: '1.125rem',
        },
    },
    menuItem: {
        marginTop: '50px',
        display: 'flex',
        fontWeight: 500,
        fontSize: '16px',
        columnGap: '10px',
        alignItems: 'center',
    },
    switch: {
        display: 'flex',
        alignItems: 'center',
        fontSize: '14px',
        marginLeft: '30px',
        marginTop: '15px',
        justifyContent: 'space-between',
    },
    switchText: {
        display: 'flex',
        flexDirection: 'column',
    },
    description: {
        color: theme.palette.text.gray,
    },
}));

const Preferences = () => {
    const classes = useStyles();
    const [state, setState] = useState({
        openInNewTab: false,
        notificationSoundEnabled: true,
        settings: [],
    });

    const { userSettings } = useContext(UserContext);
    const { userPreferences = [], userPreferencesCall } = userSettings ?? {};
    const {
        openInNewTab,
        notificationSoundEnabled,
    } = state;

    useEffect(() => {
        if (userPreferences.length > 0) {
            const newTabSetting = userPreferences.find((s) => s.key === PREFERENCES_KEY.OPENING_BEHAVIOR);
            const notificationSoundSetting = userPreferences.find((s) => s.key === PREFERENCES_KEY.NOTIFICATION_SOUND_ENABLED);
            const inNewTab = newTabSetting?.value ?? 'false';
            const soundEnabled = notificationSoundSetting?.value ?? 'false';

            setState((prevState) => ({
                ...prevState,
                openInNewTab: inNewTab === 'true',
                notificationSoundEnabled: soundEnabled === 'true',
                settings: userPreferences,
            }));
        }
    }, [userPreferences]);

    const [updateUserPreferences] = useMutation(SettingsMutation.SAVE_USER_PREFERENCES, {
        onCompleted: (response) => {
            if (response) userPreferencesCall();
        },
        onError: (error) => {
            ModalUtils.errorMessage(null, error);
        },
    });

    const onChange = (key, value) => {
        if (!userPreferencesCall) return;

        setState((prevState) => {
            const { settings } = state;
            const isNewTabKey = key === 'openInNewTab';
            const preferencesKey = isNewTabKey ? PREFERENCES_KEY.OPENING_BEHAVIOR : PREFERENCES_KEY.NOTIFICATION_SOUND_ENABLED;

            updateUserPreferences({
                variables: {
                    ...(settings.length > 0 ? { userSettingId: settings.find((s) => s.key === preferencesKey)?.userSettingId } : {}),
                    key: preferencesKey,
                    value: value ? 'true' : 'false',
                },
            });

            return {
                ...prevState,
                [key]: value,
            };
        });
    };

    return (
        <Grid container className={classes.box}>
            <div className={classes.header}>
                <Typography variant="h4">
                    Preferences
                </Typography>
            </div>
            <Grid item xs={12}>
                <div className={classes.menuItem}>
                    <MenuOutlinedIcon />
                    <span>Side Menu</span>
                </div>
                <Grid item md={5} xs={12}>
                    <div className={classes.switch}>
                        Open Modules in a new Tab
                        <Switch
                            checked={openInNewTab}
                            onChange={(event) => onChange('openInNewTab', event.target.checked)}
                            inputProps={{ 'aria-label': 'primary checkbox' }}
                        />
                    </div>
                </Grid>
            </Grid>
            <Grid item xs={12}>
                <div className={classes.menuItem}>
                    <NotificationsNoneOutlinedIcon />
                    <span>Notifications</span>
                </div>
                <Grid item md={5} xs={12}>
                    <div className={classes.switch}>
                        <div className={classes.switchText}>
                            <span>Play sound for notifications</span>
                            <span className={classes.description}>
                                Receive audio alerts for new deals, upcoming tasks, incoming messages, and important system notices.
                            </span>
                        </div>
                        <Switch
                            checked={notificationSoundEnabled}
                            onChange={(event) => onChange('notificationSoundEnabled', event.target.checked)}
                            inputProps={{ 'aria-label': 'primary checkbox' }}
                        />
                    </div>
                </Grid>
            </Grid>
        </Grid>
    );
};

export default Preferences;
